import { QTableColumn } from 'quasar'
import type { Component, MaybeRef, Ref } from 'vue'
import { inject, InjectionKey, markRaw, provide, ref, unref } from 'vue'

import enums from '@/plugins/enums'

// Types for dashboard columns
export type DashboardColumn = QTableColumn & {
  component?: ReturnType<typeof import('vue').defineComponent>
  visible?: boolean
  isMain?: boolean
  value?: unknown
  type: string
  filter?: string
  showFilter?: boolean
  props?: Record<string, unknown>
  selectOptions?: unknown[]
  headerComponent?: ReturnType<typeof import('vue').defineComponent>
  validation?: unknown[]
}

interface ColumnOptions {
  field?: MaybeRef<string>
  filter?: MaybeRef<string>
  sortable?: MaybeRef<boolean>
  visible?: MaybeRef<boolean>
  showFilter?: MaybeRef<boolean>
  align?: 'left' | 'center' | 'right'
  component?: Component
  props?: Record<string, unknown>
  selectOptions?: MaybeRef<unknown[]>
  headerComponent?: Component
  validation?: unknown[]
  [key: string]: unknown
}

type ViewKey = 'allRequestsTab' | 'valuersWorkflow'

// Define dashboardKey here to avoid circular dependency with @/keys
export const dashboardKey: InjectionKey<ReturnType<typeof useBaseDashboard>> = Symbol('dashboard')

function createDashboardBuilder() {
  const columns = new Map<string, DashboardColumn>()
  const columnOrder: Ref<string[]> = ref([]) // Store column order state
  const config = {
    columns: ref<DashboardColumn[]>([]),
    render: ref(false),
    pagination: ref({
      sortBy: 'modified_at',
      descending: true,
      rowsPerPage: 0, // 0 means all rows, because we use virtual scroll
    }),
    loading: ref(false),
    modules: ref<string[]>([
      enums.modules.DVM,
      enums.modules.OVM,
      enums.modules.OEPC,
      enums.modules.ERS,
      enums.modules.CAT,
    ]),
    excludedModules: ref<string[]>([]),
    view: {
      allRequestsTab: ref(true),
      valuersWorkflow: ref(true),
    },
  }

  return {
    getConfig() {
      return { ...config }
    },

    registerColumn(column: DashboardColumn) {
      columns.set(column.name, column)

      // Add to order if not already present
      if (!columnOrder.value.includes(column.name)) {
        columnOrder.value.push(column.name)
      }
    },

    unregisterColumn(name: string) {
      columns.delete(name)

      // Remove from order
      const index = columnOrder.value.indexOf(name)
      if (index !== -1) {
        columnOrder.value.splice(index, 1)
      }
    },

    changeOrder(fields: string | string[], targetIndex: number) {
      // Ensure fields is an array
      const fieldsArray = Array.isArray(fields) ? fields : [fields]

      // Remove the fields from their current positions
      const newOrder = columnOrder.value.filter((colName) => !fieldsArray.includes(colName))

      // Ensure target index is within bounds
      const safeTargetIndex = Math.min(Math.max(0, targetIndex), newOrder.length)

      // Insert fields at the target position
      newOrder.splice(safeTargetIndex, 0, ...fieldsArray.filter((field) => columns.has(field)))

      // Update column order
      columnOrder.value = newOrder
    },

    build() {
      // Use the columnOrder to determine the order of columns
      const orderedColumns: DashboardColumn[] = []

      // First add columns that are in the order array
      for (const name of columnOrder.value) {
        if (columns.has(name)) {
          orderedColumns.push(columns.get(name)!)
        }
      }

      // Then add any remaining columns that might not be in the order array
      for (const [name, column] of columns.entries()) {
        if (!columnOrder.value.includes(name)) {
          orderedColumns.push(column)
        }
      }

      config.columns.value = orderedColumns
      config.render.value = true
    },
  }
}

export function useBaseDashboard(root = true) {
  if (!root) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dashboard: any = inject(dashboardKey)
    if (!dashboard) {
      throw new Error('dashboard context is not reachable')
    }
    return dashboard
  }

  const builder = createDashboardBuilder()
  const config = builder.getConfig()

  const setLoading = (state: boolean) => {
    config.loading.value = state
  }

  const setModules = (state: string[]) => {
    if (state.length) {
      config.excludedModules.value = []
    }
    config.modules.value = state
  }

  const setExcludedModules = (state: string[]) => {
    if (state.length) {
      config.modules.value = []
    }
    config.excludedModules.value = state
  }

  const setView = (key: ViewKey, state: boolean) => {
    if (!config.view[key]) {
      console.warn('Undefined view key')
      return
    }
    config.view[key].value = state
  }

  const api = {
    // Column registration methods
    registerColumn({
      type,
      name,
      label = '',
      options = {},
    }: {
      type: string
      name: string
      label?: string
      options?: ColumnOptions
    }) {
      const column: DashboardColumn = {
        ...options,
        type,
        name,
        label,
        filter: unref(options.filter) || name, // if filter field is different from display field
        field: unref(options.field) || name,
        sortable: unref(options.sortable) !== false,
        visible: unref(options.visible) !== false,
        showFilter: unref(options.showFilter) ?? unref(options.visible) !== false,
        align: options.align || 'left',
        component: options.component ? markRaw(options.component) : null,
        props: options.props || {},
        selectOptions: unref(options.selectOptions), // if available, render re-select instead of re-input
        headerComponent: options.headerComponent ? markRaw(options.headerComponent) : null, // custom header component
        // Enhanced validation support for dashboard column filters
        // Supports Quasar-compatible validation rules with compact error display
        // For details: https://github.com/rockestate/rockestate_vue/pull/4713
        validation: options.validation || [],
      }
      builder.registerColumn(column)
      return this
    },

    registerCustomColumn({
      name,
      label,
      filter,
      component,
      options = {},
    }: {
      name: string
      label: string
      filter?: string
      component: Component
      options?: ColumnOptions
    }) {
      return this.registerColumn({
        type: 'custom',
        name,
        label,
        options: {
          filter,
          component,
          ...options,
        },
      })
    },

    unregisterColumn(name: string) {
      builder.unregisterColumn(name)
    },

    changeOrder(fields: string | string[], targetIndex: number) {
      builder.changeOrder(fields, targetIndex)
    },

    setLoading,
    setModules,
    setExcludedModules,
    setView,

    get isLoading() {
      return config.loading
    },

    get columns() {
      return config.columns
    },

    get modules() {
      return config.modules
    },

    get excludedModules() {
      return config.excludedModules
    },

    get view() {
      return config.view
    },

    // Get combined config with meta data
    getConfig() {
      return builder.getConfig()
    },

    renderDashboard() {
      builder.build()
    },

    pagination: config.pagination.value,
  }

  provide(dashboardKey, api)

  return api
}
